import React, { useState, useEffect } from 'react';
import { Shield, Key, Cpu, Activity } from 'lucide-react';

const STATUS_MESSAGES = [
  { text: 'Initializing secure connection...', icon: Cpu, color: 'text-blue-500' },
  { text: 'Verifying session tokens...', icon: Key, color: 'text-indigo-500' },
  { text: 'Re-authenticating credentials...', icon: Shield, color: 'text-cyan-600' },
  { text: 'Synchronizing user environment...', icon: Activity, color: 'text-emerald-650' }
];

export default function Loader3D() {
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const ActiveIcon = STATUS_MESSAGES[statusIndex].icon;

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden animated-bg font-sans select-none">
      {/* 3D Stylesheet injected dynamically */}
      <style>{`
        .animated-bg {
          background: #f5f5f5; /* whitesmoke */
        }
        .bg-glow-blue {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .bg-glow-purple {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.04) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .loader-3d-container {
          perspective: 1000px;
          transform-style: preserve-3d;
        }
        .scene-3d {
          transform-style: preserve-3d;
        }
        .ring-3d {
          position: absolute;
          border-radius: 50%;
          transform-style: preserve-3d;
        }
        .ring-outer {
          width: 220px;
          height: 220px;
          border: 2px solid rgba(59, 130, 246, 0.5);
          box-shadow: 0 0 25px rgba(59, 130, 246, 0.15), inset 0 0 25px rgba(59, 130, 246, 0.1);
          animation: spinOuter 6s linear infinite;
        }
        .ring-diagonal-1 {
          width: 185px;
          height: 185px;
          border: 1.5px dashed rgba(6, 182, 212, 0.55);
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.12);
          animation: spinDiag1 7s linear infinite;
        }
        .ring-middle {
          width: 150px;
          height: 150px;
          border: 2px dashed rgba(139, 92, 246, 0.55);
          box-shadow: 0 0 25px rgba(139, 92, 246, 0.15), inset 0 0 15px rgba(139, 92, 246, 0.08);
          animation: spinMiddle 5s linear infinite;
        }
        .ring-diagonal-2 {
          width: 120px;
          height: 120px;
          border: 1.5px dotted rgba(236, 72, 153, 0.55);
          box-shadow: 0 0 18px rgba(236, 72, 153, 0.1);
          animation: spinDiag2 4.5s linear infinite;
        }
        .ring-inner {
          width: 90px;
          height: 90px;
          border: 1px double rgba(6, 182, 212, 0.7);
          box-shadow: 0 0 35px rgba(6, 182, 212, 0.25);
          animation: spinInner 4s linear infinite;
        }
        .core-glow {
          width: 46px;
          height: 46px;
          background: radial-gradient(circle, rgba(56,189,248,1) 0%, rgba(59,130,246,1) 70%, rgba(37,99,235,0.4) 100%);
          border-radius: 50%;
          box-shadow: 0 0 25px rgba(56, 189, 248, 0.7),
                      0 0 45px rgba(59, 130, 246, 0.35);
          transform: translateZ(0);
          animation: pulseCore 1.5s ease-in-out infinite alternate;
        }
        
        /* 3D grid backplate */
        .grid-backplate {
          position: absolute;
          width: 900px;
          height: 900px;
          background-image: 
            linear-gradient(to right, rgba(59, 130, 246, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(59, 130, 246, 0.06) 1px, transparent 1px);
          background-size: 30px 30px;
          transform: rotateX(75deg) translateZ(-180px);
          mask-image: radial-gradient(circle, black 25%, transparent 70%);
          -webkit-mask-image: radial-gradient(circle, black 25%, transparent 70%);
        }

        /* Scanning Laser Beam */
        .laser-scanner {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(56, 189, 248, 0.1) 20%, rgba(56, 189, 248, 0.45) 50%, rgba(56, 189, 248, 0.1) 80%, transparent);
          box-shadow: 0 0 15px rgba(56, 189, 248, 0.35);
          z-index: 1;
          animation: laserScan 5s linear infinite;
          pointer-events: none;
        }

        @keyframes laserScan {
          0% { top: -10%; }
          100% { top: 110%; }
        }

        @keyframes spinOuter {
          0% { transform: rotateX(65deg) rotateY(0deg) rotateZ(0deg); }
          100% { transform: rotateX(65deg) rotateY(360deg) rotateZ(0deg); }
        }
        @keyframes spinDiag1 {
          0% { transform: rotateX(40deg) rotateY(40deg) rotateZ(0deg); }
          100% { transform: rotateX(40deg) rotateY(40deg) rotateZ(-360deg); }
        }
        @keyframes spinMiddle {
          0% { transform: rotateX(-50deg) rotateY(0deg) rotateZ(0deg); }
          100% { transform: rotateX(-50deg) rotateY(-360deg) rotateZ(0deg); }
        }
        @keyframes spinDiag2 {
          0% { transform: rotateX(-30deg) rotateY(55deg) rotateZ(0deg); }
          100% { transform: rotateX(-30deg) rotateY(55deg) rotateZ(360deg); }
        }
        @keyframes spinInner {
          0% { transform: rotateX(25deg) rotateY(0deg) rotateZ(0deg); }
          100% { transform: rotateX(25deg) rotateY(360deg) rotateZ(360deg); }
        }
        @keyframes pulseCore {
          0% {
            transform: scale(0.9) translateZ(0);
            box-shadow: 0 0 20px rgba(56, 189, 248, 0.5), 0 0 35px rgba(59, 130, 246, 0.2);
          }
          100% {
            transform: scale(1.05) translateZ(0);
            box-shadow: 0 0 35px rgba(56, 189, 248, 0.8), 0 0 60px rgba(59, 130, 246, 0.5);
          }
        }
        @keyframes loaderProgressBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .loader-progress-bar-anim {
          animation: loaderProgressBar 6s linear infinite;
        }
      `}</style>

      {/* Futuristic Background Gradients & Accents */}
      <div className="bg-glow-blue" />
      <div className="bg-glow-purple animate-pulse" style={{ animationDuration: '6s' }} />

      {/* Cyberpunk grid & Laser backdrops */}
      <div className="grid-backplate pointer-events-none z-0" />
      <div className="laser-scanner" />

      {/* Main 3D Gyroscope Stage Container */}
      <div className="loader-3d-container flex items-center justify-center w-80 h-80 relative z-10">
        <div className="scene-3d flex items-center justify-center w-full h-full relative">
          
          {/* Outer Ring */}
          <div className="ring-3d ring-outer flex items-center justify-center">
            <div className="absolute top-0 w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.6)] transform -translate-y-1" />
          </div>

          {/* Gyro Diagonal Ring 1 */}
          <div className="ring-3d ring-diagonal-1 flex items-center justify-center">
            <div className="absolute top-0 left-0 w-2 h-2 bg-cyan-500 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.6)]" />
          </div>

          {/* Middle Ring */}
          <div className="ring-3d ring-middle flex items-center justify-center">
            <div className="absolute right-0 w-2 h-2 bg-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.6)] transform translate-x-1" />
          </div>

          {/* Gyro Diagonal Ring 2 */}
          <div className="ring-3d ring-diagonal-2 flex items-center justify-center">
            <div className="absolute bottom-0 right-0 w-2 h-2 bg-pink-500 rounded-full shadow-[0_0_8px_rgba(236,72,153,0.6)]" />
          </div>

          {/* Inner Ring */}
          <div className="ring-3d ring-inner flex items-center justify-center">
            <div className="absolute bottom-0 w-1.5 h-1.5 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.6)] transform translate-y-0.5" />
          </div>

          {/* Center core with text "MP-X" */}
          <div className="core-glow relative z-20 flex items-center justify-center text-[10px] font-black text-white tracking-widest pl-[1.5px]">
            MP-X
          </div>
        </div>
      </div>

      {/* Status & Message Box */}
      <div className="w-full max-w-sm px-6 text-center mt-12 relative z-10">
        <div className="backdrop-blur-xl bg-white/70 border border-slate-200/80 rounded-3xl p-6 shadow-[0_15px_30px_rgba(0,0,0,0.05)] relative overflow-hidden">
          
          {/* Top subtle scanning bar */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-60 animate-pulse" />

          {/* Dynamic Status Icons + Messages */}
          <div className="h-20 flex flex-col justify-center items-center">
            <div
              key={statusIndex}
              className="flex flex-col items-center transition-all duration-300 transform scale-100 opacity-100"
            >
              <div className={`p-2.5 rounded-2xl bg-white border border-slate-100 mb-3.5 ${STATUS_MESSAGES[statusIndex].color} shadow-sm`}>
                <ActiveIcon size={22} className="animate-pulse" />
              </div>
              <p className="text-slate-800 font-bold text-sm tracking-wide">
                {STATUS_MESSAGES[statusIndex].text}
              </p>
            </div>
          </div>

          {/* Glowing bottom progress bar indicator */}
          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden border border-slate-200/60 mt-5">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-400 loader-progress-bar-anim"
            />
          </div>
        </div>

        {/* Small branding text */}
        <p className="text-slate-400 text-[10px] font-extrabold tracking-widest uppercase mt-6">
          MP-X Engine v3.1 • Secure Session Manager
        </p>
      </div>
    </div>
  );
}
